import { SERVICE_BASE_URLS } from "../global/constants";
import {
  ICloneResult,
  ICloneSources,
  ICloneTargets,
  IForeignClusters,
  IForeignSources,
  IImageCheck,
  IRebuildPlan,
  IRebuildResult,
  IRebuildVerify,
  IRestoreWindow,
  ISpecAdoptResult,
  ISpecPlan,
} from "../types/api/restore.type";
import api from "./api";

const ROOT = `${SERVICE_BASE_URLS.DB_MANAGER}/db-environments`;

/** Read-only: the recorded spec against the cluster. */
const specPlan = () => api.get<ISpecPlan>(`${ROOT}/spec/plan`);

/**
 * Write the live spec into the record, for environments with no record or a stale one.
 *
 * Touches no Kubernetes object. This is the first-rollout backfill: until it has run, a
 * rebuild has nothing to rebuild from. `userId` is required because this acts across every
 * tenant, so the server records the caller rather than checking ownership.
 *
 * Omitting `envIds` records every adoptable environment, with an explicit flag; an empty
 * array records nothing, as for `rebuild`.
 */
const specAdopt = (userId: string, envIds?: number[]) =>
  api.post<ISpecAdoptResult>(
    `${ROOT}/spec/adopt`,
    envIds === undefined ? { all_adoptable: true } : { env_ids: envIds },
    { headers: { "user-id": userId } }
  );

/** Read-only: which environments can be rebuilt, and which cannot. */
const rebuildPlan = () => api.get<IRebuildPlan>(`${ROOT}/rebuild/plan`);

/**
 * Recreate missing databases from their recorded spec, under their original ids.
 *
 * Omitting `envIds` rebuilds the whole `missing` bucket — the DR path — and says so with an
 * explicit flag. An empty array means *nothing*: it used to fall through to "everything"
 * because both sides tested the list for truthiness, so a selection filtered down to zero
 * would have rebuilt the estate. The server refuses a request that says neither.
 */
const rebuild = (userId: string, envIds?: number[]) =>
  api.post<IRebuildResult>(
    `${ROOT}/rebuild`,
    envIds === undefined ? { all_missing: true } : { env_ids: envIds },
    { headers: { "user-id": userId } }
  );

/**
 * Clear the delete mark on an environment that is staying after all.
 *
 * A delete stamps the record before destroying anything, so one that fails partway leaves a
 * database the rebuild refuses to touch — otherwise "Rebuild all" would recreate something
 * someone had just asked to destroy. This is the operator saying it is staying. Accounts only;
 * no Kubernetes object is touched.
 */
const keepEnvironment = (userId: string, envId: number) =>
  api.post<{ env_id: number }>(
    `${ROOT}/${envId}/rebuild/keep`,
    {},
    { headers: { "user-id": userId } }
  );

/** Did the data come back, or did the pod merely restart? */
const verifyRebuild = (envId: number) =>
  api.get<IRebuildVerify>(`${ROOT}/${envId}/rebuild/verify`);

/** Environments with a backup set, from any tenant, deleted ones included (D21). `q` is a database name or id,
 *  or a tenant, location, organization or account name. */
const cloneSources = (q?: string, limit = 50) =>
  api.get<ICloneSources>(`${ROOT}/clone-sources`, { params: { limit, ...(q ? { q } : {}) } });

/** Live tenants a clone may go into, with their path and primary database (D21, D22). */
const cloneTargets = (q?: string, limit = 20) =>
  api.get<ICloneTargets>(`${SERVICE_BASE_URLS.ACCOUNTS}/tenants/clone-targets`, {
    params: { limit, ...(q ? { q } : {}) },
  });

/** From the oldest backup to the last write; a live source's end moves while it writes. */
const restoreWindow = (envId: number) =>
  api.get<IRestoreWindow>(`${ROOT}/${envId}/restore/window`);

/**
 * Creates `environment` (already created in accounts) restored from `sourceId` at `point`.
 * Resolves for every HTTP status: the shared interceptor rejects with the body alone, and the
 * caller needs the status to tell a refusal (nothing created) from an uncertain failure (D11).
 * Only a request that got no answer rejects.
 */
const cloneEnvironment = (userId: string, tenantId: number, sourceId: number, body: object) =>
  api.post<ICloneResult | { detail?: any }>(`${ROOT}/${sourceId}/clone`, body, {
    headers: { "user-id": userId, "tenant-id": String(tenantId) },
    validateStatus: () => true,
  });

/** The clusters whose backups can be cloned here (D18), and whether that is switched on. */
const foreignClusters = () => api.get<IForeignClusters>(`${ROOT}/clone-foreign`);

/** Another cluster's environments with a backup set, newest id first; `envId` finds one (D19). */
const foreignSources = (cluster: string, envId?: number, limit = 50) =>
  api.get<IForeignSources>(`${ROOT}/clone-foreign/sources`, {
    params: { cluster, limit, ...(envId !== undefined ? { env_id: envId } : {}) },
  });

/** Another cluster's source: from its oldest backup to the newest WAL in its archive. */
const foreignWindow = (cluster: string, account: string, envId: number) =>
  api.get<IRestoreWindow>(`${ROOT}/clone-foreign/window`, { params: { cluster, account, env_id: envId } });

/** As `cloneEnvironment`, from another cluster's backups; resolves for every status (D11). */
const cloneForeign = (userId: string, tenantId: number, body: object) =>
  api.post<ICloneResult | { detail?: any }>(`${ROOT}/clone-foreign`, body, {
    headers: { "user-id": userId, "tenant-id": String(tenantId) },
    validateStatus: () => true,
  });

/** D25: what is known about a tag or repository:tag, the same string the clone sends; starts a check if nothing
 *  is known, so poll while `checking`. */
const checkImage = (image: string) =>
  api.get<IImageCheck>(`${ROOT}/images/check`, { params: { image: image.trim() } });

const RestoreService = {
  specPlan,
  specAdopt,
  rebuildPlan,
  rebuild,
  keepEnvironment,
  verifyRebuild,
  cloneSources,
  cloneTargets,
  restoreWindow,
  cloneEnvironment,
  foreignClusters,
  foreignSources,
  foreignWindow,
  cloneForeign,
  checkImage,
};

export default RestoreService;
